import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type Variant = "primary" | "secondary" | "accent" | "outline" | "ghost" | "light";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-all duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500/60 disabled:opacity-50 disabled:cursor-not-allowed";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary-500 text-background-50 hover:bg-primary-600 active:scale-[0.98] shadow-card",
  secondary:
    "bg-secondary-500 text-background-50 hover:bg-secondary-600 active:scale-[0.98] shadow-card",
  accent:
    "bg-accent-500 text-foreground-950 hover:bg-accent-400 active:scale-[0.98] shadow-card",
  outline:
    "border border-primary-300 bg-transparent text-primary-800 hover:border-primary-500 hover:bg-primary-50 active:scale-[0.98]",
  ghost: "text-primary-800 hover:bg-primary-50 active:scale-[0.98]",
  light:
    "bg-background-50 text-primary-800 hover:bg-accent-100 active:scale-[0.98] shadow-card",
};

const sizes: Record<Size, string> = {
  sm: "text-sm px-4 py-2",
  md: "text-sm px-6 py-3",
  lg: "text-base px-8 py-4",
};

interface ButtonProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  type?: "button" | "submit";
  target?: string;
  rel?: string;
  ariaLabel?: string;
}

export default function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  to,
  href,
  onClick,
  type = "button",
  target,
  rel,
  ariaLabel,
}: ButtonProps) {
  const classes = `${base} ${variants[variant]} ${sizes[size]} ${className}`;

  if (to) {
    return (
      <Link to={to} className={classes} aria-label={ariaLabel} onClick={onClick}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} target={target} rel={rel} aria-label={ariaLabel} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <button type={type} className={classes} onClick={onClick} aria-label={ariaLabel}>
      {children}
    </button>
  );
}